import type { VoiceConnectionState, VoiceMediaAuthority } from '../voice-types';
import {
  AudioAnalysisTracker,
  configureVoiceAudioAnalyser,
  type AudioAnalysisFrame,
} from '../media/audio-analysis';
import { RealtimeEchoGuard } from './realtime-echo-guard';

export type OpenAIRealtimeTransportErrorCode =
  | 'already_connected'
  | 'closed'
  | 'invalid_media_topology'
  | 'negotiation_failed'
  | 'provider_quota_exhausted'
  | 'ice_failed'
  | 'peer_failed'
  | 'output_device_failed'
  | 'playback_failed'
  | 'microphone_replace_failed'
  | 'microphone_gate_failed'
  | 'user_gesture_required';

export class OpenAIRealtimeTransportError extends Error {
  constructor(
    readonly code: OpenAIRealtimeTransportErrorCode,
    readonly recoverable: boolean,
    message: string,
  ) {
    super(message);
    this.name = 'OpenAIRealtimeTransportError';
  }
}

export interface OpenAIRealtimeTransportDependencies {
  createPeerConnection: () => RTCPeerConnection;
  createAudioContext: () => AudioContext;
  requestAnimationFrame: (callback: FrameRequestCallback) => number;
  cancelAnimationFrame: (handle: number) => void;
  queueMicrotask: (callback: () => void) => void;
}

export interface OpenAIRealtimeTransportOptions {
  dependencies?: Partial<OpenAIRealtimeTransportDependencies>;
  onConnectionState: (state: VoiceConnectionState) => void;
  onInputLevel: (level: number) => void;
  onOutputLevel: (level: number) => void;
  onInputSpectrum: (spectrum: number[]) => void;
  onOutputSpectrum: (spectrum: number[]) => void;
  onError: (error: OpenAIRealtimeTransportError) => void;
}

export interface OpenAIRealtimeConnectInput {
  authority: VoiceMediaAuthority;
  stream: MediaStream;
  outputElement: HTMLAudioElement;
  outputDeviceId?: string;
  prearmedContext?: AudioContext;
  negotiateOffer: (
    authority: VoiceMediaAuthority,
    offerSdp: string,
  ) => Promise<string>;
}

export interface OpenAIRealtimeResumeInput {
  userGestureActive: boolean;
  stream?: MediaStream;
}

export interface OpenAIRealtimeTransportLike {
  connect(input: OpenAIRealtimeConnectInput): Promise<void>;
  pause(): Promise<void>;
  resume(input: OpenAIRealtimeResumeInput): Promise<void>;
  interrupt(): void;
  releaseOutput?(): void;
  setMuted?(muted: boolean): void;
  close(): Promise<void>;
}

const defaultDependencies = (): OpenAIRealtimeTransportDependencies => ({
  createPeerConnection: () => new RTCPeerConnection(),
  createAudioContext: () => new AudioContext(),
  requestAnimationFrame: (callback) => window.requestAnimationFrame(callback),
  cancelAnimationFrame: (handle) => window.cancelAnimationFrame(handle),
  queueMicrotask: (callback) => window.queueMicrotask(callback),
});

const sampleAnalysisFrame = (
  analyser: AnalyserNode | null,
  tracker: AudioAnalysisTracker,
  timestampMs: number,
): AudioAnalysisFrame => {
  const frequencyBytes = new Uint8Array(analyser?.frequencyBinCount ?? 0);
  analyser?.getByteFrequencyData(frequencyBytes);

  return tracker.sample(frequencyBytes, timestampMs);
};

const safeTrackStop = (track: MediaStreamTrack): void => {
  try {
    track.stop();
  } catch (_error: unknown) {
    return;
  }
};

export class OpenAIRealtimeTransport implements OpenAIRealtimeTransportLike {
  private readonly dependencies: OpenAIRealtimeTransportDependencies;
  private peer: RTCPeerConnection | null = null;
  private inputStream: MediaStream | null = null;
  private outputElement: HTMLAudioElement | null = null;
  private context: AudioContext | null = null;
  private inputSource: MediaStreamAudioSourceNode | null = null;
  private outputSource: MediaStreamAudioSourceNode | null = null;
  private inputAnalyser: AnalyserNode | null = null;
  private outputAnalyser: AnalyserNode | null = null;
  private meterSink: GainNode | null = null;
  private sender: RTCRtpSender | null = null;
  private eventsChannel: RTCDataChannel | null = null;
  private remoteStream: MediaStream | null = null;
  private remoteTrack: MediaStreamTrack | null = null;
  private animationFrame: number | null = null;
  private readonly inputAnalysisTracker = new AudioAnalysisTracker();
  private readonly outputAnalysisTracker = new AudioAnalysisTracker();
  private readonly echoGuard = new RealtimeEchoGuard();
  private muted = false;
  private readonly stoppedTracks = new Set<MediaStreamTrack>();
  private desiredSenderTrack: MediaStreamTrack | null = null;
  private senderGatePromise: Promise<void> = Promise.resolve();
  private closePromise: Promise<void> | null = null;
  private state: 'idle' | 'connecting' | 'connected' | 'paused' | 'closed' =
    'idle';
  private publishedConnectionState: VoiceConnectionState | null = null;
  private operationEpoch = 0;
  private outputSuppressed = false;

  constructor(private readonly options: OpenAIRealtimeTransportOptions) {
    this.dependencies = {
      ...defaultDependencies(),
      ...options.dependencies,
    };
  }

  async connect(input: OpenAIRealtimeConnectInput): Promise<void> {
    if (this.state === 'closed') {
      throw new OpenAIRealtimeTransportError(
        'closed',
        false,
        'OpenAI Realtime transport is closed',
      );
    }
    if (this.state !== 'idle') {
      throw new OpenAIRealtimeTransportError(
        'already_connected',
        false,
        'OpenAI Realtime transport is already connected',
      );
    }
    const epoch = ++this.operationEpoch;
    this.state = 'connecting';
    this.inputStream = input.stream;
    this.outputElement = input.outputElement;
    this.publishConnectionState('connecting');

    const audioTracks = input.stream.getAudioTracks();
    if (audioTracks.length !== 1 || input.stream.getTracks().length !== 1) {
      const error = new OpenAIRealtimeTransportError(
        'invalid_media_topology',
        false,
        'OpenAI Realtime requires one microphone audio track',
      );
      this.options.onError(error);
      await this.close();
      throw error;
    }

    try {
      const peer = this.dependencies.createPeerConnection();
      this.peer = peer;
      peer.addEventListener('connectionstatechange', this.onConnectionChange);
      peer.addEventListener(
        'iceconnectionstatechange',
        this.onIceConnectionChange,
      );
      peer.addEventListener('track', this.onTrack);
      peer.addEventListener('datachannel', this.onDataChannel);
      const eventsChannel = peer.createDataChannel('oai-events');
      this.eventsChannel = eventsChannel;
      eventsChannel.addEventListener('message', this.onEventsMessage);
      this.sender = peer.addTrack(audioTracks[0], input.stream);
      this.desiredSenderTrack = audioTracks[0];
      this.startMetering(input.stream, input.prearmedContext);
      await this.applyOutputDevice(input.outputDeviceId);
      if (!this.ownsConnectOperation(epoch, peer)) {
        throw new Error('Transport ownership changed during negotiation');
      }

      const offer = await peer.createOffer();
      if (!this.ownsConnectOperation(epoch, peer)) {
        throw new Error('Transport ownership changed during negotiation');
      }
      await peer.setLocalDescription(offer);
      if (!this.ownsConnectOperation(epoch, peer)) {
        throw new Error('Transport ownership changed during negotiation');
      }
      const offerSdp = peer.localDescription?.sdp;
      if (!offerSdp) throw new Error('Local offer SDP was unavailable');
      const mediaSections = offerSdp
        .split(/\r?\n/)
        .filter((line) => line.startsWith('m='));
      const audioSections = mediaSections.filter((line) =>
        line.startsWith('m=audio '),
      );
      const dataSections = mediaSections.filter((line) =>
        line.startsWith('m=application '),
      );
      const includesDataChannel = /(?:sctp-port|webrtc-datachannel)/i.test(
        offerSdp,
      );
      if (
        mediaSections.length !== 2 ||
        audioSections.length !== 1 ||
        dataSections.length !== 1 ||
        !includesDataChannel
      ) {
        throw new Error(
          'Local offer must contain one audio and one WebRTC data channel',
        );
      }
      const answerSdp = await input.negotiateOffer(input.authority, offerSdp);
      if (!this.ownsConnectOperation(epoch, peer)) {
        throw new Error('Transport ownership changed during negotiation');
      }
      if (!answerSdp || !answerSdp.startsWith('v=0')) {
        throw new Error('Provider answer SDP was invalid');
      }
      await peer.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      if (!this.ownsConnectOperation(epoch, peer)) {
        throw new Error('Transport ownership changed during negotiation');
      }
    } catch (caught: unknown) {
      const error =
        caught instanceof OpenAIRealtimeTransportError
          ? caught
          : new OpenAIRealtimeTransportError(
              'negotiation_failed',
              false,
              'OpenAI Realtime negotiation failed',
            );
      this.options.onError(error);
      await this.close();
      throw error;
    }
  }

  async pause(): Promise<void> {
    if (this.state === 'closed') return;
    const epoch = ++this.operationEpoch;
    this.echoGuard.reset();
    this.desiredSenderTrack = null;
    for (const track of this.inputStream?.getAudioTracks() ?? []) {
      this.stopTrack(track);
    }
    this.inputStream = null;
    for (const receiver of this.peer?.getReceivers() ?? []) {
      if (receiver.track) receiver.track.enabled = false;
    }
    this.outputElement?.pause();
    let suspendError: OpenAIRealtimeTransportError | null = null;
    if (this.context?.state === 'running') {
      try {
        await this.context.suspend();
      } catch (_error: unknown) {
        suspendError = new OpenAIRealtimeTransportError(
          'playback_failed',
          true,
          'OpenAI Realtime output could not pause',
        );
      }
    }
    if (!this.ownsOperation(epoch)) return;
    this.stopMetering();
    this.state = 'paused';
    if (suspendError) {
      this.options.onError(suspendError);
      throw suspendError;
    }
  }

  async resume(input: OpenAIRealtimeResumeInput): Promise<void> {
    if (!input.userGestureActive) {
      for (const track of input.stream?.getTracks() ?? [])
        this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'user_gesture_required',
        true,
        'Resume voice from a user gesture',
      );
    }
    if (this.state === 'closed') {
      for (const track of input.stream?.getTracks() ?? [])
        this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'closed',
        false,
        'OpenAI Realtime transport is closed',
      );
    }
    const epoch = ++this.operationEpoch;
    const stream = input.stream;
    const tracks = stream?.getAudioTracks() ?? [];
    if (!stream || tracks.length !== 1 || stream.getTracks().length !== 1) {
      for (const track of stream?.getTracks() ?? []) this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'invalid_media_topology',
        false,
        'OpenAI Realtime requires one fresh microphone audio track',
      );
    }
    try {
      await this.senderGatePromise;
      await this.sender?.replaceTrack(tracks[0]);
    } catch (_error: unknown) {
      for (const track of stream.getTracks()) this.stopTrack(track);
      const error = new OpenAIRealtimeTransportError(
        'microphone_replace_failed',
        false,
        'OpenAI Realtime microphone could not resume',
      );
      this.options.onError(error);
      throw error;
    }
    if (!this.ownsOperation(epoch)) {
      for (const track of stream.getTracks()) this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'closed',
        false,
        'OpenAI Realtime transport is closed',
      );
    }
    this.inputStream = stream;
    this.echoGuard.reset();
    this.desiredSenderTrack = tracks[0];
    if (this.context?.state === 'suspended') {
      try {
        await this.context.resume();
      } catch (_error: unknown) {
        const error = new OpenAIRealtimeTransportError(
          'playback_failed',
          true,
          'OpenAI Realtime output could not resume',
        );
        if (await this.rollbackResume(epoch, stream)) {
          this.options.onError(error);
          throw error;
        }
        throw this.closedError();
      }
    }
    if (!this.ownsOperation(epoch)) {
      for (const track of stream.getTracks()) this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'closed',
        false,
        'OpenAI Realtime transport is closed',
      );
    }
    this.attachInputMeter(stream);
    if (this.remoteStream) this.attachOutputMeter(this.remoteStream);
    this.startAnimationLoop();
    try {
      await this.outputElement?.play();
    } catch (_error: unknown) {
      const error = new OpenAIRealtimeTransportError(
        'playback_failed',
        true,
        'OpenAI Realtime output could not resume',
      );
      if (await this.rollbackResume(epoch, stream)) {
        this.options.onError(error);
        throw error;
      }
      throw this.closedError();
    }
    if (!this.ownsOperation(epoch)) {
      for (const track of stream.getTracks()) this.stopTrack(track);
      throw new OpenAIRealtimeTransportError(
        'closed',
        false,
        'OpenAI Realtime transport is closed',
      );
    }
    this.state = 'connected';
    this.applyOutputGate();
  }

  interrupt(): void {
    if (this.state === 'closed') return;
    const tracks = (this.peer?.getReceivers() ?? [])
      .map((receiver) => receiver.track)
      .filter((candidate): candidate is MediaStreamTrack => Boolean(candidate));
    for (const track of tracks) track.enabled = false;
    this.outputSuppressed = true;
    this.echoGuard.reset();
    this.setMicrophoneTransmission(!this.muted);
    const outputFrame = this.outputAnalysisTracker.reset();
    this.options.onOutputLevel(outputFrame.level);
    this.options.onOutputSpectrum([...outputFrame.spectrum]);
  }

  releaseOutput(): void {
    if (this.state === 'closed') return;
    this.outputSuppressed = false;
    this.applyOutputGate();
  }

  close(): Promise<void> {
    if (this.closePromise) return this.closePromise;
    if (this.state === 'closed') return Promise.resolve();
    this.closePromise = this.closeOwnedResources();
    return this.closePromise;
  }

  private async closeOwnedResources(): Promise<void> {
    this.operationEpoch += 1;
    this.state = 'closed';
    this.outputSuppressed = false;
    this.echoGuard.reset();
    this.desiredSenderTrack = null;
    if (this.animationFrame !== null) {
      this.dependencies.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    const peer = this.peer;
    this.peer = null;
    const eventsChannel = this.eventsChannel;
    this.eventsChannel = null;
    const tracks = new Set<MediaStreamTrack>(this.inputStream?.getTracks());
    for (const sender of peer?.getSenders() ?? []) {
      if (sender.track) tracks.add(sender.track);
    }
    for (const receiver of peer?.getReceivers() ?? []) {
      if (receiver.track) tracks.add(receiver.track);
    }
    for (const track of tracks) this.stopTrack(track);
    this.inputStream = null;
    this.sender = null;
    this.remoteStream = null;
    this.remoteTrack = null;
    const inputSource = this.inputSource;
    const outputSource = this.outputSource;
    const inputAnalyser = this.inputAnalyser;
    const outputAnalyser = this.outputAnalyser;
    const meterSink = this.meterSink;
    this.inputSource = null;
    this.outputSource = null;
    this.inputAnalyser = null;
    this.outputAnalyser = null;
    this.meterSink = null;

    const outputElement = this.outputElement;
    this.outputElement = null;
    const context = this.context;
    this.context = null;
    const attempts: Array<() => void | Promise<void>> = [];
    if (peer) {
      attempts.push(
        () =>
          peer.removeEventListener(
            'connectionstatechange',
            this.onConnectionChange,
          ),
        () =>
          peer.removeEventListener(
            'iceconnectionstatechange',
            this.onIceConnectionChange,
          ),
        () => peer.removeEventListener('track', this.onTrack),
        () => peer.removeEventListener('datachannel', this.onDataChannel),
      );
    }
    if (eventsChannel) {
      attempts.push(
        () =>
          eventsChannel.removeEventListener('message', this.onEventsMessage),
        () => eventsChannel.close(),
      );
    }
    if (inputSource) attempts.push(() => inputSource.disconnect());
    if (outputSource) attempts.push(() => outputSource.disconnect());
    if (inputAnalyser) attempts.push(() => inputAnalyser.disconnect());
    if (outputAnalyser) attempts.push(() => outputAnalyser.disconnect());
    if (meterSink) attempts.push(() => meterSink.disconnect());
    if (outputElement) {
      attempts.push(() => {
        outputElement.pause();
        outputElement.srcObject = null;
        outputElement.removeAttribute('src');
      });
    }
    if (context && context.state !== 'closed') {
      attempts.push(() => context.close());
    }
    if (peer) attempts.push(() => peer.close());
    const results = await Promise.allSettled(
      attempts.map((attempt) => {
        try {
          return Promise.resolve(attempt());
        } catch (cleanupError: unknown) {
          return Promise.reject(cleanupError);
        }
      }),
    );
    this.resetMeteringCallbacks();
    this.publishConnectionState('closed');
    const failure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    if (failure) throw failure.reason;
  }

  private readonly onConnectionChange = (): void => {
    const state = this.peer?.connectionState;
    if (!state || this.state === 'closed') return;
    if (state === 'connected') {
      if (this.state !== 'paused') this.state = 'connected';
      this.publishConnectionState('connected');
      return;
    }
    if (state === 'disconnected') {
      this.publishConnectionState('reconnecting');
      return;
    }
    if (state === 'failed') {
      this.failAsynchronously(
        new OpenAIRealtimeTransportError(
          'peer_failed',
          false,
          'OpenAI Realtime peer connection failed',
        ),
      );
      return;
    }
    if (state === 'closed') this.publishConnectionState('closed');
    else this.publishConnectionState('connecting');
  };

  private readonly onIceConnectionChange = (): void => {
    const state = this.peer?.iceConnectionState;
    if (!state || this.state === 'closed') return;
    if (state === 'failed') {
      this.failAsynchronously(
        new OpenAIRealtimeTransportError(
          'ice_failed',
          false,
          'OpenAI Realtime ICE connection failed',
        ),
      );
      return;
    }
    if (state === 'disconnected') this.publishConnectionState('reconnecting');
    if (state === 'connected' || state === 'completed')
      this.publishConnectionState('connected');
  };

  private readonly onTrack = (event: RTCTrackEvent): void => {
    if (this.state === 'closed' || event.track.kind !== 'audio') {
      this.stopTrack(event.track);
      return;
    }
    const stream = event.streams[0];
    const outputElement = this.outputElement;
    if (!stream || !outputElement) {
      this.stopTrack(event.track);
      return;
    }
    if (this.remoteTrack && this.remoteTrack !== event.track) {
      this.stopTrack(event.track);
      this.options.onError(
        new OpenAIRealtimeTransportError(
          'invalid_media_topology',
          false,
          'OpenAI Realtime returned more than one audio output',
        ),
      );
      return;
    }
    outputElement.removeAttribute('src');
    outputElement.autoplay = true;
    outputElement.srcObject = stream;
    this.remoteStream = stream;
    this.remoteTrack = event.track;
    this.applyOutputGate();
    if (this.state === 'paused') return;
    this.attachOutputMeter(stream);
    const epoch = this.operationEpoch;
    void outputElement.play().catch((_error: unknown) => {
      if (
        this.state === 'closed' ||
        this.state === 'paused' ||
        this.operationEpoch !== epoch ||
        this.remoteTrack !== event.track ||
        this.outputElement !== outputElement
      ) {
        return;
      }
      const error = new OpenAIRealtimeTransportError(
        'playback_failed',
        true,
        'OpenAI Realtime output could not start',
      );
      this.options.onError(error);
    });
  };

  private readonly onDataChannel = (event: RTCDataChannelEvent): void => {
    event.channel.close();
  };

  private readonly onEventsMessage = (_event: MessageEvent): void => undefined;

  private startMetering(
    inputStream: MediaStream,
    prearmedContext?: AudioContext,
  ): void {
    const context = prearmedContext ?? this.dependencies.createAudioContext();
    this.context = context;
    const meterSink = context.createGain();
    meterSink.gain.value = 0;
    meterSink.connect(context.destination);
    this.meterSink = meterSink;
    this.attachInputMeter(inputStream);
    this.startAnimationLoop();
  }

  private attachInputMeter(inputStream: MediaStream): void {
    const context = this.context;
    if (!context) return;
    this.inputSource?.disconnect();
    this.inputAnalyser?.disconnect();
    const inputAnalyser = context.createAnalyser();
    configureVoiceAudioAnalyser(inputAnalyser);
    const inputSource = context.createMediaStreamSource(inputStream);
    inputSource.connect(inputAnalyser);
    if (this.meterSink) inputAnalyser.connect(this.meterSink);
    this.inputAnalyser = inputAnalyser;
    this.inputSource = inputSource;
  }

  private startAnimationLoop(): void {
    if (this.animationFrame !== null) return;
    const sample = (timestampMs: number): void => {
      if (this.state === 'closed' || this.state === 'paused') {
        this.animationFrame = null;
        return;
      }
      const inputFrame = sampleAnalysisFrame(
        this.inputAnalyser,
        this.inputAnalysisTracker,
        timestampMs,
      );
      const outputFrame = this.outputSuppressed
        ? this.outputAnalysisTracker.reset()
        : sampleAnalysisFrame(
            this.outputAnalyser,
            this.outputAnalysisTracker,
            timestampMs,
          );
      this.setMicrophoneTransmission(
        !this.muted && this.echoGuard.shouldTransmit(inputFrame, outputFrame),
      );
      this.options.onInputLevel(inputFrame.level);
      this.options.onOutputLevel(outputFrame.level);
      this.options.onInputSpectrum([...inputFrame.spectrum]);
      this.options.onOutputSpectrum([...outputFrame.spectrum]);
      this.animationFrame = this.dependencies.requestAnimationFrame(sample);
    };
    this.animationFrame = this.dependencies.requestAnimationFrame(sample);
  }

  private stopMetering(): void {
    if (this.animationFrame !== null) {
      this.dependencies.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.inputSource?.disconnect();
    this.outputSource?.disconnect();
    this.inputAnalyser?.disconnect();
    this.outputAnalyser?.disconnect();
    this.inputSource = null;
    this.outputSource = null;
    this.inputAnalyser = null;
    this.outputAnalyser = null;
    this.resetMeteringCallbacks();
  }

  private attachOutputMeter(stream: MediaStream): void {
    const context = this.context;
    if (!context) return;
    this.outputSource?.disconnect();
    this.outputAnalyser?.disconnect();
    const analyser = context.createAnalyser();
    configureVoiceAudioAnalyser(analyser);
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    if (this.meterSink) analyser.connect(this.meterSink);
    this.outputAnalyser = analyser;
    this.outputSource = source;
  }

  private async applyOutputDevice(deviceId?: string): Promise<void> {
    const outputElement = this.outputElement;
    const sink = outputElement as HTMLAudioElement & {
      setSinkId?: (value: string) => Promise<void>;
    };
    if (!deviceId || typeof sink.setSinkId !== 'function') return;
    try {
      await sink.setSinkId(deviceId);
    } catch (_error: unknown) {
      this.options.onError(
        new OpenAIRealtimeTransportError(
          'output_device_failed',
          true,
          'Saved voice output device is unavailable; using the default',
        ),
      );
    }
  }

  private failAsynchronously(error: OpenAIRealtimeTransportError): void {
    this.publishConnectionState('failed');
    this.options.onError(error);
    void this.close().catch(() => undefined);
  }

  private stopTrack(track: MediaStreamTrack): void {
    if (this.stoppedTracks.has(track)) return;
    this.stoppedTracks.add(track);
    safeTrackStop(track);
  }

  /** Mute means only that the model cannot hear the user; playback continues. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.setMicrophoneTransmission(false);
  }

  private setMicrophoneTransmission(enabled: boolean): void {
    if (this.state === 'closed' || this.state === 'paused') return;
    const sender = this.sender;
    if (!sender) return;
    const desiredTrack = enabled
      ? (this.inputStream?.getAudioTracks()[0] ?? null)
      : null;
    if (this.desiredSenderTrack === desiredTrack) return;
    this.desiredSenderTrack = desiredTrack;
    const epoch = this.operationEpoch;
    this.senderGatePromise = this.senderGatePromise.then(async () => {
      if (
        this.state === 'closed' ||
        this.state === 'paused' ||
        this.operationEpoch !== epoch ||
        this.sender !== sender ||
        this.desiredSenderTrack !== desiredTrack ||
        sender.track === desiredTrack
      ) {
        return;
      }
      try {
        await sender.replaceTrack(desiredTrack);
      } catch (_error: unknown) {
        if (
          this.ownsOperation(epoch) &&
          this.sender === sender &&
          this.desiredSenderTrack === desiredTrack
        ) {
          this.options.onError(
            new OpenAIRealtimeTransportError(
              'microphone_gate_failed',
              true,
              'Voice echo suppression is unavailable',
            ),
          );
        }
      }
    });
  }

  private async rollbackResume(
    epoch: number,
    stream: MediaStream,
  ): Promise<boolean> {
    if (!this.ownsOperation(epoch)) {
      for (const track of stream.getTracks()) this.stopTrack(track);
      return false;
    }
    for (const track of stream.getTracks()) this.stopTrack(track);
    if (this.inputStream === stream) this.inputStream = null;
    this.echoGuard.reset();
    this.desiredSenderTrack = null;
    for (const receiver of this.peer?.getReceivers() ?? []) {
      if (receiver.track) receiver.track.enabled = false;
    }
    this.outputElement?.pause();
    if (this.context?.state === 'running') {
      try {
        await this.context.suspend();
      } catch (_error: unknown) {
        // The microphone and receiver gates below are authoritative even when
        // the browser cannot suspend its metering context.
      }
    }
    if (!this.ownsOperation(epoch)) return false;
    this.stopMetering();
    this.state = 'paused';
    return true;
  }

  private resetMeteringCallbacks(): void {
    const inputFrame = this.inputAnalysisTracker.reset();
    const outputFrame = this.outputAnalysisTracker.reset();
    this.options.onInputLevel(inputFrame.level);
    this.options.onOutputLevel(outputFrame.level);
    this.options.onInputSpectrum([...inputFrame.spectrum]);
    this.options.onOutputSpectrum([...outputFrame.spectrum]);
  }

  private applyOutputGate(): void {
    const enabled = this.state !== 'paused' && !this.outputSuppressed;
    for (const receiver of this.peer?.getReceivers() ?? []) {
      if (receiver.track) receiver.track.enabled = enabled;
    }
    if (this.remoteTrack) this.remoteTrack.enabled = enabled;
  }

  private closedError(): OpenAIRealtimeTransportError {
    return new OpenAIRealtimeTransportError(
      'closed',
      false,
      'OpenAI Realtime transport is closed',
    );
  }

  private ownsOperation(epoch: number): boolean {
    return this.state !== 'closed' && this.operationEpoch === epoch;
  }

  private ownsConnectOperation(
    epoch: number,
    peer: RTCPeerConnection,
  ): boolean {
    return (
      (this.state === 'connecting' || this.state === 'connected') &&
      this.operationEpoch === epoch &&
      this.peer === peer
    );
  }

  private publishConnectionState(state: VoiceConnectionState): void {
    if (this.publishedConnectionState === state) return;
    this.publishedConnectionState = state;
    this.options.onConnectionState(state);
  }
}
